import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from "../../Context/AuthContext";
import "../../Styling/UsageDashboard.css";
import { Base_URL } from '../../url';
import { Base_URL2 } from '../../url';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// New API import
import { getCompanyByOrganisationId } from "../../Api/IncomingPo";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Icon Components
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
  </svg>
);

const ExpandIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"/>
  </svg>
);

const CollapseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z"/>
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
  </svg>
);

// Helper Functions
const toCrore = (amount) => {
  if (!amount) return 0;
  return (amount / 10000000).toFixed(2);
};

const extractCountsFromData = (data) => {
  let employeeCount = 0;
  let inventoryGRNCount = 0;
  let salesCount = 0;
  let projectCount = 0;
  let ticketCount = 0;
  let taskCount = 0;

  data.forEach(org => {
    org.acitDetails?.forEach(detail => {
      switch(detail.accounttype3) {
        case 'EmployeeSalary':
          employeeCount += detail.reourceIdCount || 0;
          break;
        case 'InventoryGRN':
          inventoryGRNCount += detail.reourceIdCount || 0;
          break;
        case 'Sales':
          salesCount += detail.reourceIdCount || 0;
          break;
        case 'Project':
          projectCount += detail.reourceIdCount || 0;
          break;
        case 'Ticket':
          ticketCount += detail.reourceIdCount || 0;
          break;
        case 'Task':
          taskCount += detail.reourceIdCount || 0;
          break;
        default:
          break;
      }
    });
  });

  return {
    totalEmployeeCount: employeeCount,
    totalInventoryGRNCount: inventoryGRNCount,
    totalSalesCount: salesCount,
    totalProjectCount: projectCount,
    totalTicketCount: ticketCount,
    totalTaskCount: taskCount
  };
};

// Weekly Document Graph Component
const WeeklyDocumentGraph = ({ 
  data, 
  loading, 
  year, 
  onYearChange,
  companyCodes,
  companyCodesLoading,
  filters,
  onFilterChange 
}) => {
  // Show loading state
  if (loading) {
    return (
      <div className="graph-loading-state">
        <div className="usage-loading-spinner" />
        <p>Loading graph data...</p>
      </div>
    );
  }

  // Show message when no company is selected
  if (!filters.companyCode) {
    return (
      <div className="graph-empty-state">
        <div className="empty-icon">📊</div>
        <h3>Select a Company</h3>
        <p>Please select a company from the dropdown to view graph data.</p>
      </div>
    );
  }

  // Show message when no organization is selected
if (!filters.organisationId) {
  return (
    <div className="graph-empty-state">
      <div className="empty-icon">🏢</div>
      <h3>Select an Organization</h3>
      <p>Please select an organization to view graph data.</p>
    </div>
  );
}

if (!filters.companyCode) {
  return (
    <div className="graph-empty-state">
      <div className="empty-icon">📊</div>
      <h3>Select a Company</h3>
      <p>Please select a company from the dropdown to view graph data.</p>
    </div>
  );
}



  const chartData = {
    labels: data.map(item => `W${item.weekNumber}`),
    datasets: [
      {
        label: 'Documents Posted',
        data: data.map(item => item.documentPosted),
        borderColor: 'rgb(74, 107, 255)',
        backgroundColor: 'rgba(74, 107, 255, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: 'rgb(74, 107, 255)',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#1e293b',
          font: {
            size: 11,
            family: "'Inter', sans-serif"
          },
          padding: 16,
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: 'rgba(30, 41, 59, 0.9)',
        titleColor: 'white',
        bodyColor: 'white',
        titleFont: {
          size: 11,
          family: "'Inter', sans-serif"
        },
        bodyFont: {
          size: 10,
          family: "'Inter', sans-serif"
        },
        padding: 10,
        cornerRadius: 6,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const weekNum = context.label.replace('W', '');
            return `Week ${weekNum}: ${context.raw} documents`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(226, 232, 240, 0.3)',
          drawBorder: false
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 9,
            family: "'Inter', sans-serif"
          },
          maxTicksLimit: 8
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(226, 232, 240, 0.3)',
          drawBorder: false
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 9,
            family: "'Inter', sans-serif"
          },
          callback: function(value) {
            return value;
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    elements: {
      line: {
        cubicInterpolationMode: 'monotone'
      }
    }
  };

  return (
    <div className="weekly-document-graph">
      <div className="graph-header">
        <h3>Weekly Document Posting</h3>
        <div className="graph-filters">
          <div className="filter-group">
            {/* <select 
              className="filter-select"
              value={filters.companyCode}
              onChange={(e) =>
  onFilterChange('companyCode', Number(e.target.value))
}


              disabled={companyCodes.length === 0 || companyCodesLoading}
            >
              {companyCodesLoading ? (
                <option value="">Loading companies...</option>
              ) : companyCodes.length === 0 ? (
                <option value="">No company codes</option>
              ) : (
                <>
                  <option value="">Select Company</option>
                  {companyCodes.map(company => (
                    <option key={company.companyCode} value={company.companyCode}>
                      {company.companyCode} - {company.companyName}
                    </option>
                  ))}
                </>
              )}
            </select> */}
          </div>
          
          <div className="year-selector">
            <button 
              className="year-nav-btn"
              onClick={() => onYearChange(year - 1)}
              disabled={loading}
            >
              &larr;
            </button>
            <span className="current-year">{year}</span>
            <button 
              className="year-nav-btn"
              onClick={() => onYearChange(year + 1)}
              disabled={loading}
            >
              &rarr;
            </button>
          </div>
        </div>
      </div>
      
      <div className="graph-container">
        <Line data={chartData} options={options} />
      </div>
      
      <div className="graph-footer">
        <div className="total-documents">
          <span className="total-label">Total:</span>
          <span className="total-value">
            {data.reduce((sum, item) => sum + item.documentPosted, 0).toLocaleString()}
          </span>
        </div>
        <div className="graph-stats">
          <span className="stat-item">
            Peak: {Math.max(...data.map(item => item.documentPosted))}
          </span>
          <span className="stat-item">
            Avg: {Math.round(data.reduce((sum, item) => sum + item.documentPosted, 0) / data.length)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Main UsageDashboard Component
const UsageDashboard = () => {
  const { user, token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [orgList, setOrgList] = useState([]);
  const [tableUsage, setTableUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [tableSortConfig, setTableSortConfig] = useState({ key: 'totalSpaceMb', direction: 'desc' });
  const [exporting, setExporting] = useState(false);
  const dashboardRef = useRef();

  // Graph-related state
  const [graphData, setGraphData] = useState([]);
  const [graphLoading, setGraphLoading] = useState(true);
  const [graphYear, setGraphYear] = useState(new Date().getFullYear());
  const [companyCodes, setCompanyCodes] = useState([]);
  const [companyCodesLoading, setCompanyCodesLoading] = useState(false);
  const [graphFilters, setGraphFilters] = useState({
  organisationId: null,
  companyCode: null
});


  const counts = extractCountsFromData(orgList);

  // Initial data fetching
  useEffect(() => {
    fetchUsageDashboardData();
    fetchTableUsageData();
  }, []);

useEffect(() => {
  if (user?.organisationId) {
    setGraphFilters(prev => ({
      ...prev,
      organisationId: Number(user.organisationId)
    }));
    fetchCompanyCodes(user.organisationId); // sets companyCode internally
  }
}, [user]);



  // MAIN FIX: This useEffect watches for changes in organizationId, companyCode, AND year
  useEffect(() => {
    console.log('useEffect triggered with:', {
      orgId: graphFilters.organisationId,
      companyCode: graphFilters.companyCode,
      year: graphYear,
      shouldFetch: graphFilters.organisationId && graphFilters.companyCode
    });
    
   if (graphFilters.organisationId && graphFilters.companyCode) {
  fetchWeeklyDocumentGraph();
} else {
  setGraphData([]);
  setGraphLoading(false);
}

  }, [graphFilters.organisationId, graphFilters.companyCode, graphYear]);

  const fetchCompanyCodes = async (orgId) => {
  try {
    setCompanyCodesLoading(true);
    setCompanyCodes([]);

    const companyData = await getCompanyByOrganisationId(orgId);

    let companies = Array.isArray(companyData) ? companyData
                  : companyData?.data ? companyData.data
                  : [companyData];

    const formattedCompanies = companies.map(c => ({
      companyCode: Number(c.companyCode || c.code || c.id),
      companyName: c.companyName || c.name || 'Unknown Company'
    }));

    setCompanyCodes(formattedCompanies);

setGraphFilters(prev => ({
  ...prev,
  companyCode: formattedCompanies.length > 0 ? formattedCompanies[0].companyCode : null
}));


  } catch (error) {
    console.error(error);
    setCompanyCodes([]);
    setGraphFilters({ organisationId: orgId, companyCode: null });
  } finally {
    setCompanyCodesLoading(false);
  }
};


  // Fetch weekly document graph data
  const fetchWeeklyDocumentGraph = async () => {
    try {
      if (!graphFilters.organisationId || !graphFilters.companyCode) {
        console.log('Missing required filters:', graphFilters);
        setGraphData([]);
        setGraphLoading(false);
        return;
      }
      
      setGraphLoading(true);
      
      console.log('Fetching graph data with params:', {
        year: graphYear,
        organisationId: graphFilters.organisationId,
        companyCode: graphFilters.companyCode
      });
      console.log("FINAL GRAPH PARAM TYPES", {
  orgId: graphFilters.organisationId,
  orgType: typeof graphFilters.organisationId,
  companyCode: graphFilters.companyCode,
  companyType: typeof graphFilters.companyCode
});

      const response = await axios.get(
  `${Base_URL2}/usageDashboard/weekly-document-graph`,
  {
    params: {
      year: Number(graphYear),
      organisationId: Number(graphFilters.organisationId),
      companyCode: Number(graphFilters.companyCode)
    }
    
  }
  
  
);

      
      console.log('Graph API Response:', response.data);
      
      // Format the data for the chart
      if (Array.isArray(response.data)) {
        setGraphData(response.data);
      } else if (response.data && Array.isArray(response.data.data)) {
        setGraphData(response.data.data);
      } else if (response.data && response.data.weeklyData) {
        setGraphData(response.data.weeklyData);
      } else {
        console.log('No graph data found in response');
        setGraphData([]);
      }
      
    } catch (error) {
      console.error("Error fetching graph data:", error);
      console.error("Error details:", error.response?.data || error.message);
      setGraphData([]);
    } finally {
      setGraphLoading(false);
    }
  };

  // Fetch main dashboard data
  const fetchUsageDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${Base_URL2}/usageDashboard/GetUsageDashboardData`);
      console.log('Dashboard API Response:', response.data);
      setSummary(response.data?.summary || null);
      setOrgList(response.data?.data || []);
      
      const initialExpandedState = {};
      response.data?.data?.forEach((_, index) => {
        initialExpandedState[index] = true;
      });
      setExpandedOrgs(initialExpandedState);
    } catch (error) {
      console.error("Error fetching usage dashboard data:", error);
      setSummary(null);
      setOrgList([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch table usage data
  const fetchTableUsageData = async () => {
    try {
      setTableLoading(true);
      console.log('Fetching table usage from:', `${Base_URL}/usageDashboard/table-usage`);
      const response = await axios.get(`${Base_URL2}/usageDashboard/table-usage`);
      console.log('Table Usage API Response:', response.data);
      
      let tableData = [];
      if (Array.isArray(response.data)) {
        tableData = response.data;
      } else if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data.data)) {
          tableData = response.data.data;
        } else if (Array.isArray(response.data.tables)) {
          tableData = response.data.tables;
        } else if (Array.isArray(response.data.result)) {
          tableData = response.data.result;
        } else {
          tableData = Object.values(response.data);
        }
      }
      
      console.log('Processed Table Data:', tableData);
      setTableUsage(tableData);
    } catch (error) {
      console.error("Error fetching table usage data:", error);
      console.error("Error details:", error.response?.data || error.message);
      setTableUsage([]);
    } finally {
      setTableLoading(false);
    }
  };

  // Helper functions
  const toggleOrgSection = (index) => {
    setExpandedOrgs(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleAllSections = (expand) => {
    const newState = {};
    orgList.forEach((_, index) => {
      newState[index] = expand;
    });
    setExpandedOrgs(newState);
  };

  const exportToPDF = async () => {
    try {
      setExporting(true);
      const originalTitle = document.title;
      document.title = "Generating PDF...";

      const element = dashboardRef.current;
      element.classList.add('usage-pdf-export');
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        removeContainer: true,
        windowWidth: 1200,
        width: element.scrollWidth,
        height: element.scrollHeight,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector('.usage-dashboard__container');
          if (clonedElement) {
            clonedElement.style.padding = '20px';
            clonedElement.style.background = '#ffffff';
          }
        }
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.9);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = pdfHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();

      while (heightLeft >= 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }

      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Page ${i} of ${totalPages} • Generated on ${new Date().toLocaleDateString()}`,
          pdf.internal.pageSize.getWidth() / 2,
          pdf.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      pdf.save(`usage-dashboard-${new Date().toISOString().split('T')[0]}.pdf`);
      element.classList.remove('usage-pdf-export');
      document.title = originalTitle;
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const filteredOrgs = orgList.filter(org => 
    org?.orgDetails?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedOrgs = React.useMemo(() => {
    if (!sortConfig.key) return filteredOrgs;
    
    return [...filteredOrgs].sort((a, b) => {
      const aValue = a.orgDetails?.[sortConfig.key] || 0;
      const bValue = b.orgDetails?.[sortConfig.key] || 0;
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredOrgs, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleTableSort = (key) => {
    setTableSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedTables = React.useMemo(() => {
    console.log('Sorting tables with data:', tableUsage);
    if (!Array.isArray(tableUsage) || tableUsage.length === 0) return [];
    
    return [...tableUsage].sort((a, b) => {
      const aValue = a[tableSortConfig.key] || 0;
      const bValue = b[tableSortConfig.key] || 0;
      
      if (tableSortConfig.direction === 'desc') {
        if (bValue > aValue) return 1;
        if (bValue < aValue) return -1;
        return 0;
      } else {
        if (aValue > bValue) return 1;
        if (aValue < bValue) return -1;
        return 0;
      }
    }).slice(0, 10);
  }, [tableUsage, tableSortConfig]);

  const getOrgCountByType = (acitDetails, accountType) => {
    const detail = acitDetails?.find(d => d.accounttype3 === accountType);
    return detail?.reourceIdCount || 0;
  };

  const formatMB = (value) => {
    if (!value && value !== 0) return '0.00';
    return Number(value).toFixed(2);
  };

  const getTableIcon = (tableName) => {
    if (!tableName) return '📋';
    const lowerName = tableName.toLowerCase();
    if (lowerName.includes('ticket')) return '🎫';
    if (lowerName.includes('order')) return '📦';
    if (lowerName.includes('acit')) return '💰';
    if (lowerName.includes('employee')) return '👥';
    if (lowerName.includes('project')) return '📋';
    if (lowerName.includes('task')) return '✅';
    if (lowerName.includes('sales')) return '📈';
    if (lowerName.includes('inventory')) return '📊';
    if (lowerName.includes('customer')) return '👤';
    if (lowerName.includes('product')) return '📦';
    return '📋';
  };

  const getTableColor = (size) => {
    const numSize = Number(size) || 0;
    if (numSize > 10) return '#ff6b6b';
    if (numSize > 5) return '#ff922b';
    if (numSize > 1) return '#ffd43b';
    if (numSize > 0.5) return '#51cf66';
    return '#339af0';
  };

  const getTotalDatabaseSize = () => {
    if (!Array.isArray(tableUsage)) {
      console.log('tableUsage is not an array:', tableUsage);
      return 0;
    }
    
    const total = tableUsage.reduce((total, table) => {
      const size = Number(table.totalSpaceMb) || 0;
      return total + size;
    }, 0);
    
    console.log('Total database size calculated:', total);
    return total;
  };

  // Loading and error states
  if (loading) {
    return (
      <div className="usage-dashboard__container">
        <div className="usage-dashboard__loading">
          <div className="usage-loading-spinner" />
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="usage-dashboard__container">
        <div className="usage-dashboard__empty">
          <div className="usage-empty-state">
            <h3>No Data Available</h3>
            <p>Unable to load dashboard data. Please try again later.</p>
            <button onClick={fetchUsageDashboardData} className="usage-retry-btn">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalDatabaseSize = getTotalDatabaseSize();
  const hasTableData = Array.isArray(tableUsage) && tableUsage.length > 0;

  return (
    <div className="usage-dashboard__container" ref={dashboardRef}>
      {/* Header with Actions */}
      <div className="usage-dashboard__header">
        <div className="usage-header-content">
          <h1 className="usage-dashboard__title">Usage Dashboard</h1>
          <p className="usage-dashboard__subtitle">
            {summary.totalOrgs > 0 && ` • ${summary.totalOrgs} organizations monitored`}
          </p>
        </div>
        <div className="usage-header-actions">
          <div className="usage-search-box">
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="usage-search-input"
            />
            <FilterIcon />

          </div>
          
          <button 
            className="usage-btn-secondary"
            onClick={() => toggleAllSections(true)}
            disabled={orgList.length === 0}
          >
            Expand All
          </button>
          <button 
            className="usage-btn-secondary"
            onClick={() => toggleAllSections(false)}
            disabled={orgList.length === 0}
          >
            Collapse All
          </button>
          <button 
            className="usage-btn-primary"
            onClick={exportToPDF}
            disabled={exporting || orgList.length === 0}
          >
            {exporting ? (
              <>
                <div className="usage-loading-spinner-small" />
                Generating...
              </>
            ) : (
              <>
                <DownloadIcon />
                Export PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Summary Cards Grid */}
      <div className="usage-summary-cards__grid">
        <div className="usage-summary-card">
          <div className="usage-card-icon">🏢</div>
          <div className="usage-card-content">
            <h3 className="usage-card-title">Total Organizations</h3>
            <p className="usage-card-value">{summary.totalOrgs}</p>
          </div>
        </div>

        <div className="usage-summary-card">
          <div className="usage-card-icon">👥</div>
          <div className="usage-card-content">
            <h3 className="usage-card-title">Active Users</h3>
            <p className="usage-card-value">{summary.totalActiveUsers}</p>
          </div>
        </div>

        <div className="usage-summary-card">
          <div className="usage-card-icon">💰</div>
          <div className="usage-card-content">
            <h3 className="usage-card-title">Employee Salary</h3>
            <p className="usage-card-value">{toCrore(summary.totalEmployeeSalary)} <span>Cr</span></p>
            <div className="usage-card-count usage-card-count--small">
              {counts.totalEmployeeCount} employees
            </div>
          </div>
        </div>

        <div className="usage-summary-card">
          <div className="usage-card-icon">📦</div>
          <div className="usage-card-content">
            <h3 className="usage-card-title">Total GRN</h3>
            <p className="usage-card-value">{toCrore(summary.totalInventoryGRN)} <span>Cr</span></p>
            <div className="usage-card-count usage-card-count--small">
              {counts.totalInventoryGRNCount} documents
            </div>
          </div>
        </div>

        <div className="usage-summary-card">
          <div className="usage-card-icon">📈</div>
          <div className="usage-card-content">
            <h3 className="usage-card-title">Total Sales</h3>
            <p className="usage-card-value">{toCrore(summary.totalSales)} <span>Cr</span></p>
            <div className="usage-card-count usage-card-count--small">
              {counts.totalSalesCount} documents
            </div>
          </div>
        </div>

        <div className="usage-summary-card">
          <div className="usage-card-icon">📋</div>
          <div className="usage-card-content">
            <h3 className="usage-card-title">Total Projects</h3>
            <p className="usage-card-value">{summary.totalProjects || 0}</p>
            <div className="usage-card-count usage-card-count--small">
              {counts.totalProjectCount || 0} active
            </div>
          </div>
        </div>

        <div className="usage-summary-card">
          <div className="usage-card-icon">🎫</div>
          <div className="usage-card-content">
            <h3 className="usage-card-title">Total Tickets</h3>
            <p className="usage-card-value">{summary.totalTickets || 0}</p>
            <div className="usage-card-count usage-card-count--small">
              {counts.totalTicketCount || 0} pending
            </div>
          </div>
        </div>

        <div className="usage-summary-card">
          <div className="usage-card-icon">✅</div>
          <div className="usage-card-content">
            <h3 className="usage-card-title">Total Tasks</h3>
            <p className="usage-card-value">{summary.totalTasks || 0}</p>
            <div className="usage-card-count usage-card-count--small">
              {counts.totalTaskCount || 0} completed
            </div>
          </div>
        </div>
      </div>

      {/* Database Space Section */}
      <div className="database-space-container">
        <div className="space-container-header">
          <h2>Database Space Occupied by Top 10 Tables</h2>
          <p className="space-container-subtitle">
            Total database size: <strong>{formatMB(totalDatabaseSize)} MB</strong>
            {hasTableData && ` • ${tableUsage.length} tables found`}
          </p>
        </div>

        {tableLoading ? (
          <div className="table-loading-state">
            <div className="usage-loading-spinner" />
            <p>Loading table usage data...</p>
          </div>
        ) : !hasTableData ? (
          <div className="table-empty-state">
            <div className="empty-icon">📊</div>
            <h3>No Table Data Available</h3>
            <p>
              {Array.isArray(tableUsage) 
                ? 'Table data is empty.' 
                : `Expected array but got: ${typeof tableUsage}`}
            </p>
            <button onClick={fetchTableUsageData} className="usage-btn-secondary">
              Retry Loading Tables
            </button>
            <div className="debug-info">
              <p>API Endpoint: {Base_URL}/usageDashboard/table-usage</p>
              <p>Data type: {typeof tableUsage}</p>
              <p>Is Array: {Array.isArray(tableUsage) ? 'Yes' : 'No'}</p>
              <p>Check console for API response details.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="space-controls">
              <div className="sort-controls">
                <span className="sort-label">Sort by:</span>
                <button 
                  className={`sort-btn ${tableSortConfig.key === 'totalSpaceMb' ? 'active' : ''}`}
                  onClick={() => handleTableSort('totalSpaceMb')}
                >
                  Total Space {tableSortConfig.key === 'totalSpaceMb' && (tableSortConfig.direction === 'asc' ? '↑' : '↓')}
                </button>
                <button 
                  className={`sort-btn ${tableSortConfig.key === 'rowCount' ? 'active' : ''}`}
                  onClick={() => handleTableSort('rowCount')}
                >
                  Row Count {tableSortConfig.key === 'rowCount' && (tableSortConfig.direction === 'asc' ? '↑' : '↓')}
                </button>
                <button 
                  className={`sort-btn ${tableSortConfig.key === 'dataSpaceMb' ? 'active' : ''}`}
                  onClick={() => handleTableSort('dataSpaceMb')}
                >
                  Data Space {tableSortConfig.key === 'dataSpaceMb' && (tableSortConfig.direction === 'asc' ? '↑' : '↓')}
                </button>
              </div>
            </div>

            {/* Scrollable Container */}
            <div className="tables-container-wrapper">
              <div className="tables-grid">
                {sortedTables.map((table, index) => {
                  const efficiency = table.totalSpaceMb > 0 
                    ? ((table.dataSpaceMb / table.totalSpaceMb) * 100).toFixed(1)
                    : '0.0';
                  const tableColor = getTableColor(table.totalSpaceMb);
                  
                  return (
                    <div 
                      key={table.tableName || index} 
                      className="table-card"
                      style={{ borderLeftColor: tableColor }}
                    >
                      <div className="table-card-header">
                        <div className="table-rank">
                          <span className="rank-number">#{index + 1}</span>
                        </div>
                        <div className="table-icon">
                          {getTableIcon(table.tableName)}
                        </div>
                        <div className="table-name-container">
                          <h3 className="table-name">{table.tableName || 'Unknown Table'}</h3>
                          <div className="table-efficiency">
                            <span className="efficiency-label">Efficiency:</span>
                            <span className="efficiency-value">{efficiency}%</span>
                          </div>
                        </div>
                      </div>

                      <div className="table-stats">
                        <div className="table-stat">
                          <div className="stat-icon">📊</div>
                          <div className="stat-content">
                            <div className="stat-label">Rows</div>
                            <div className="stat-value">{table.rowCount?.toLocaleString() || '0'}</div>
                          </div>
                        </div>

                        <div className="table-stat">
                          <div className="stat-icon">💾</div>
                          <div className="stat-content">
                            <div className="stat-label">Total Space</div>
                            <div className="stat-value">{formatMB(table.totalSpaceMb)} MB</div>
                          </div>
                        </div>
                      </div>

                      <div className="space-breakdown">
                        <div className="space-labels">
                          <div className="space-label">
                            <span className="label-dot data-dot"></span>
                            Data: {formatMB(table.dataSpaceMb)} MB
                          </div>
                          <div className="space-label">
                            <span className="label-dot unused-dot"></span>
                            Unused: {formatMB(table.unusedSpaceMb)} MB
                          </div>
                        </div>
                        
                        <div className="space-bar">
                          <div 
                            className="space-fill data-space"
                            style={{ width: table.totalSpaceMb > 0 ? `${(table.dataSpaceMb / table.totalSpaceMb) * 100}%` : '0%' }}
                            title={`Data: ${formatMB(table.dataSpaceMb)} MB`}
                          ></div>
                          <div 
                            className="space-fill unused-space"
                            style={{ width: table.totalSpaceMb > 0 ? `${(table.unusedSpaceMb / table.totalSpaceMb) * 100}%` : '0%' }}
                            title={`Unused: ${formatMB(table.unusedSpaceMb)} MB`}
                          ></div>
                        </div>
                      </div>

                      <div className="table-card-footer">
                        <div className="table-size-indicator" style={{ backgroundColor: tableColor }}>
                          {table.totalSpaceMb > 10 ? 'Large' : 
                           table.totalSpaceMb > 5 ? 'Medium' : 
                           table.totalSpaceMb > 1 ? 'Small' : 'Tiny'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Weekly Document Graph Section */}
      <div className="weekly-graph-container">
        <div className="graph-section-header">
          <h2>Document Activity Graph</h2>
          <div className="org-selector">
<select
  className="org-select"
  value={graphFilters.organisationId ?? ""}
  onChange={(e) => {
    const value = e.target.value;

    if (value === "") {
      setGraphFilters({ organisationId: null, companyCode: null });
      setCompanyCodes([]);
      return;
    }

    const orgId = Number(value); // Now this will be a proper number
    if (isNaN(orgId)) return;

    setGraphFilters(prev => ({
      ...prev,
      organisationId: orgId,
      companyCode: null
    }));

    fetchCompanyCodes(orgId);
  }}
>
  <option value="">Select Organization</option>
  {orgList.map(org => (
    <option
      key={org.orgDetails.organisationId}
      value={org.orgDetails.organisationId} // <-- FIXED: use numeric ID
    >
      {org.orgDetails.name} // Display name for user
    </option>
  ))}
</select>



          </div>
        </div>
        
        <WeeklyDocumentGraph 
          data={graphData}
          loading={graphLoading}
          year={graphYear}
          onYearChange={(newYear) => {
            // Simply update the year - the useEffect will handle the fetch
            setGraphYear(newYear);
          }}
          companyCodes={companyCodes}
          companyCodesLoading={companyCodesLoading}
          filters={graphFilters}
          onFilterChange={(key, value) => {
            const newFilters = {
              ...graphFilters,
              [key]: value
            };
            setGraphFilters(newFilters);
          }}
        />
      </div>



      {/* Organization Sections */}
      <div className="usage-org-sections">
        <div className="usage-org-sections-header">
          <div className="usage-org-header-left">
            <h2>Organization Details</h2>
            <span className="usage-org-count">
              {filteredOrgs.length} of {orgList.length} organizations
              {searchTerm && ` matching "${searchTerm}"`}
            </span>
          </div>
          <div className="usage-sort-options">
            <span>Sort by: </span>
            <button 
              className={`usage-sort-btn ${sortConfig.key === 'activeUser' ? 'active' : ''}`}
              onClick={() => handleSort('activeUser')}
            >
              Users {sortConfig.key === 'activeUser' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </button>
          </div>
        </div>

        {sortedOrgs.length === 0 ? (
          <div className="usage-no-results">
            <p>No organizations found matching your search.</p>
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="usage-btn-secondary"
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          sortedOrgs.map((o, index) => {
            const org = o?.orgDetails;
            const acits = o?.acitDetails || [];
            const isExpanded = expandedOrgs[index];

            const employeeCount = getOrgCountByType(acits, 'EmployeeSalary');
            const grnCount = getOrgCountByType(acits, 'InventoryGRN');
            const salesCount = getOrgCountByType(acits, 'Sales');
            const projectCount = getOrgCountByType(acits, 'Project');
            const ticketCount = getOrgCountByType(acits, 'Ticket');
            const taskCount = getOrgCountByType(acits, 'Task');

            return (
              <section key={index} className={`usage-org-section ${isExpanded ? 'expanded' : 'collapsed'}`}>
                <div 
                  className="usage-org-section-header"
                  onClick={() => toggleOrgSection(index)}
                >
                  <div className="usage-org-header-content">
                    <h2 className="usage-org-title">{org?.name}</h2>
                    <div className="usage-org-badges">
                      <span className="usage-user-badge">
                        👥 {org?.activeUser} active users
                      </span>
                      <span className="usage-acit-badge">
                        📊 {acits.length} ACIT entries
                      </span>
                      <span className="usage-salary-badge">
                        💰 Total: {toCrore(acits.reduce((sum, acit) => sum + (acit.totalSalaryPaid || 0), 0))} Cr
                      </span>
                      {employeeCount > 0 && (
                        <span className="usage-count-badge">
                          👨‍💼 {employeeCount} employees
                        </span>
                      )}
                      {grnCount > 0 && (
                        <span className="usage-count-badge">
                          📋 {grnCount} GRN docs
                        </span>
                      )}
                      {salesCount > 0 && (
                        <span className="usage-count-badge">
                          🧾 {salesCount} sales docs
                        </span>
                      )}
                      {projectCount > 0 && (
                        <span className="usage-count-badge">
                          📁 {projectCount} projects
                        </span>
                      )}
                      {ticketCount > 0 && (
                        <span className="usage-count-badge">
                          🎫 {ticketCount} tickets
                        </span>
                      )}
                      {taskCount > 0 && (
                        <span className="usage-count-badge">
                          ✅ {taskCount} tasks
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="usage-org-toggle">
                    {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="usage-org-content">
                    <div className="usage-financial-metrics__grid">
                      <div className="usage-metric-card usage-primary-metric">
                        <h3 className="usage-metric-title">Active Users</h3>
                        <p className="usage-metric-value">{org?.activeUser}</p>
                        <div className="usage-metric-trend">Current Active</div>
                      </div>

                      {acits.map((a, i) => (
                        <div key={i} className="usage-metric-card">
                          <h3 className="usage-metric-title">{a.gldescription}</h3>
                          <p className="usage-card-value">{toCrore(a.totalSalaryPaid)} <span>Cr</span></p>
                          <div className="usage-card-trend">
                            {a.accounttype3 === 'EmployeeSalary' && `${a.reourceIdCount || 0} employees`}
                            {a.accounttype3 === 'InventoryGRN' && `${a.reourceIdCount || 0} GRN documents`}
                            {a.accounttype3 === 'Sales' && `${a.reourceIdCount || 0} sales documents`}
                            {a.accounttype3 === 'Project' && `${a.reourceIdCount || 0} projects`}
                            {a.accounttype3 === 'Ticket' && `${a.reourceIdCount || 0} tickets`}
                            {a.accounttype3 === 'Task' && `${a.reourceIdCount || 0} tasks`}
                            {!['EmployeeSalary', 'InventoryGRN', 'Sales', 'Project', 'Ticket', 'Task'].includes(a.accounttype3) && 'Salary Paid'}
                          </div>
                        </div>
                      ))}

                      {acits.length === 0 && (
                        <div className="usage-metric-card usage-empty-metric">
                          <h3 className="usage-metric-title">No ACIT Data</h3>
                          <p className="usage-metric-value">-</p>
                          <div className="usage-metric-subtitle">No financial data available</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </section>
            );
          })
        )}
      </div>
    </div>
  );
};

export default UsageDashboard;